import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RadarChart } from "../components/dashboard/charts";
import LottieLoader from "../components/dashboard/lottieLoader";
import {
  calculateEEATScore,
  resetErrors,
  scrapeArticle,
  selectEEATState,
  setUrl,
} from "../slices/eeat";
import { AppDispatch } from "../store";

const placeholderData = {
  labels: ["Experience", "Expertise", "Authoritativeness", "Trustworthiness"],
  datasets: [
    {
      label: "Your Article",
      data: [3, 6, 4, 7], // Example placeholder values
    },
  ],
};

// Helper function to determine color based on score
const getScoreColor = (score?: number) => {
  if (score === undefined) return "#cccccc";
  if (score < 4) return "#FF0000"; // Red for 0-3.9
  if (score < 8) return "#f59e0b"; // Orange/Yellow for 4-7.9
  return "#4CAF50"; // Green for 8-10
};
// Circular progress component with score
const ScoreCircle = ({
  score,
  size = 20,
}: {
  score?: number;
  size?: number;
}) => {
  const color = getScoreColor(score);
  const normalizedValue =
    score !== undefined ? Math.min(Math.max(score * 10, 0), 100) : 0;

  return (
    <Box
      position="relative"
      display="inline-flex"
      sx={{ width: size, height: size }}
    >
      <CircularProgress
        variant="determinate"
        value={normalizedValue}
        size={size}
        thickness={6}
        sx={{
          color: color,
          "& .MuiCircularProgress-circle": {
            strokeLinecap: "round",
          },
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: size * 0.7,
            height: size * 0.7,
            borderRadius: "50%",
            backgroundColor: "white",
          }}
        />
      </Box>
    </Box>
  );
};
// Skeleton loader for chart
const ChartSkeleton = () => (
  <Box sx={{ width: "100%", maxWidth: 400, mx: "auto", mb: 2, mt: 2 }}>
    <Skeleton variant="circular" width={320} height={320} sx={{ mx: "auto" }} />
  </Box>
);

// Helper to parse the new API response
function parseEEATApiResponse(apiResult: any) {
  if (!apiResult || !apiResult.eeat_score) return null;

  const article = apiResult.eeat_score?.article;
  const articleId = apiResult?.article_id;
  const articleTitle = apiResult?.article_title;
  const articleMetaDescription = apiResult?.article_meta_description;

  return {
    scores: article.scores,
    overall: article.overall,
    articleId: articleId,
    articleTitle: articleTitle,
    articleMetaDescription: articleMetaDescription,
  };
}

const EEAT: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { url, loading, error, scrapedText, result } =
    useSelector(selectEEATState);
  const [urlInput, setUrlInput] = useState("");
  const [isValidUrl, setIsValidUrl] = useState(true);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [parsedResult, setParsedResult] = useState<any>();

  // Validate URL format
  const validateUrl = (text: string) => {
    try {
      new URL(text);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Handle URL input change
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setUrlInput(inputValue);
    setIsValidUrl(
      inputValue === "" ||
        validateUrl(
          inputValue.startsWith("http") ? inputValue : `https://${inputValue}`
        )
    );
  };

  // Handle Check My Score button click
  const handleCheckScore = async () => {
    const formattedUrl = urlInput.startsWith("http")
      ? urlInput
      : `https://${urlInput}`;
    if (validateUrl(formattedUrl)) {
      setHasSubmitted(true);
      dispatch(resetErrors());
      dispatch(setUrl(formattedUrl));
      try {
        const scrapeResult = await dispatch(
          scrapeArticle(formattedUrl)
        ).unwrap();
        let articleText = scrapeResult.article_text || scrapeResult.data[0];

        const articleTitle = scrapeResult.data[1];
        const articleMetaDescription = scrapeResult.data[2];
        if (articleTitle) {
          articleText = `# ${articleTitle}\n\n${articleText}`;
        }
        if (scrapeResult.success && articleText) {
          const eeatResult = await dispatch(
            calculateEEATScore({
              article_text: articleText,
              article_link: formattedUrl,
              article_title: articleTitle,
              article_meta_description: articleMetaDescription,
            })
          ).unwrap();
          setParsedResult(parseEEATApiResponse(eeatResult));
        }
      } catch (error) {
        setParsedResult(null);
        console.error("Error processing the request", error);
      }
    } else {
      setIsValidUrl(false);
    }
  };

  // Handle See How to Improve My Score button click
  const handleSeeHowToImprove = () => {
    // Generate or use article ID - using cleaned URL as a fallback if no proper ID exists
    const formattedUrl = urlInput.startsWith("http")
      ? urlInput
      : `https://${urlInput}`;
    const articleId = parsedResult?.articleId || formattedUrl;
    const articleTitle = parsedResult?.articleTitle || "";
    const articleMetaDescription = parsedResult?.articleMetaDescription || "";

    // Send a message to the parent window (seowind.io)
    window.parent.postMessage(
      {
        type: "EEAT_SEE_IMPROVE",
        articleId: articleId,
        articleTitle: articleTitle,
        articleMetaDescription: articleMetaDescription,
      },
      "*" // Will be replaced with your actual domain in production
    );
  };

  // Chart data for the result view
  const chartData = parsedResult
    ? {
        labels: [
          "Experience",
          "Expertise",
          "Authoritativeness",
          "Trustworthiness",
        ],
        datasets: [
          {
            label: "Your Article",
            data: [
              parsedResult.scores.experience,
              parsedResult.scores.expertise,
              parsedResult.scores.authoritativeness,
              parsedResult.scores.trustworthiness,
            ],
          },
        ],
      }
    : placeholderData;

  // Show loading state
  const isLoading = loading.scrape || loading.analyze;

  // Render the input view when no results available
  const renderInputView = () => (
    <>
      <Typography
        sx={{
          fontWeight: 500,
          fontFamily: "Inter",
          fontSize: "18px",
          mb: "0.5rem",
          color: "#121828",
          textAlign: "left",
        }}
      >
        EEAT Score Checker
      </Typography>
      <Typography
        sx={{
          color: "#7B85AD",
          mb: "1rem",
          fontSize: "15px",
          fontFamily: "Inter",
          fontWeight: 400,
          textAlign: "left",
        }}
      >
        Curious how your article stacks up? Drop your URL and get your EEAT
        score instantly. No strings attached.
      </Typography>
      <Box
        sx={{
          mb: 3,
        }}
      >
        {/* Show error alerts if any */}
        {(error.scrape || error.analyze) && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => dispatch(resetErrors())}
          >
            {error.scrape || error.analyze}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Enter your article URL"
          name="url"
          type="text"
          value={urlInput}
          onChange={handleUrlChange}
          error={!isValidUrl}
          helperText={!isValidUrl ? "Please enter a valid URL" : ""}
          disabled={isLoading}
          sx={{
            mb: 2.5,
            "& .MuiInputLabel-root": {
              color: "#5E6692",
              fontFamily: "Inter",
              fontSize: "15px",
            },
            "& .MuiOutlinedInput-root": {
              borderRadius: "14px",
              height: "56px",
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#7B85AD",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6A00FF",
                borderWidth: "1px",
              },
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#D8DEF3",
            },
          }}
          variant="outlined"
        />
        <Button
          fullWidth
          variant="contained"
          onClick={handleCheckScore}
          disabled={isLoading || !urlInput || !isValidUrl}
          sx={{
            mt: "0.5rem",
            backgroundImage:
              "linear-gradient(86.04deg,#FFD875 -18.56%, #DA00C8 35.8%, #6A00FF 106.2%)",
            color: "#fff !important",
            fontWeight: 400,
            fontSize: "1.1rem",
            borderRadius: "10px",
            py: 1.5,
            boxShadow: "0 8px 16px 0 rgba(218, 0, 200, 0.15)",
            mb: 2,
            textTransform: "none",
            fontFamily: "Inter",
            "&:hover": {
              backgroundImage:
                "linear-gradient(86.04deg,#FFD875 -18.56%, #DA00C8 35.8%, #6A00FF 106.2%)",
              boxShadow: "0 8px 16px 0 rgba(218, 0, 200, 0.25)",
            },
          }}
        >
          {isLoading ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
              {loading.scrape ? "Scraping URL..." : "Analyzing..."}
            </Box>
          ) : (
            "Check My Score"
          )}
        </Button>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <InfoOutlinedIcon sx={{ color: "#919EAB", fontSize: 18, mr: 1 }} />
          <Typography
            sx={{
              color: "#919EAB",
              fontSize: "14px",
              fontFamily: "Inter",
              fontWeight: 400,
            }}
          >
            Free. No credit card required.
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ my: 3, borderColor: "rgba(145, 158, 171, 0.15)" }} />
      <Typography
        sx={{
          fontWeight: 500,
          fontFamily: "Inter",
          fontSize: "18px",
          mb: "0.5rem",
          color: "#121828",
          textAlign: "left",
        }}
      >
        What you'll get?
      </Typography>
      <Typography
        sx={{
          color: "#7B85AD",
          mb: "1rem",
          fontSize: "15px",
          fontFamily: "Inter",
          fontWeight: 400,
          textAlign: "left",
        }}
      >
        See how your content ranks on Experience, Expertise, Authority, and
        Trust. Get a clear score plus a path to upgrade your article with
        AI-powered suggestions and automated improvements.
      </Typography>

      {/* Show skeleton or chart */}
      {hasSubmitted && isLoading ? (
        <LottieLoader />
      ) : (
        <Box sx={{ width: "100%", maxWidth: 400, mx: "auto", mb: 2 }}>
          <RadarChart
            data={placeholderData}
            height={320}
            width={"100%"}
            showLegend={false}
            usePurpleColor={true}
          />
        </Box>
      )}
    </>
  );

  // Render the results view when results are available
  const renderResultView = () => (
    <>
      <Typography
        sx={{
          fontWeight: 500,
          fontFamily: "Inter",
          fontSize: "18px",
          mb: "0.5rem",
          color: "#121828",
          textAlign: "left",
        }}
      >
        Your EEAT Score Is Ready
      </Typography>
      <Typography
        sx={{
          color: "#7B85AD",
          //   mb: "0.2rem",
          fontSize: "15px",
          fontFamily: "Inter",
          fontWeight: 400,
          textAlign: "left",
        }}
      >
        Here's your score across Experience, Expertise, Authority, and Trust.
        Want to improve it? Let our AI do the work for you.
      </Typography>

      {/* Chart */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          mx: "auto",
          mt: "-1rem",
          mb: "1rem",
        }}
      >
        <RadarChart
          data={chartData}
          height={320}
          width={"100%"}
          showLegend={false}
          usePurpleColor={true}
        />
      </Box>

      {/* Score box */}
      <Box
        sx={{
          borderRadius: "8px",
          background: "#F9FAFC",
          mt: "1rem",
          mb: "1rem",
          p: "1rem",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          //   boxShadow: "0 2px 8px 0 rgba(80,72,229,0.04)",
          height: "100px",
        }}
      >
        <Typography
          sx={{
            color: "#7B85AD",
            fontSize: "15px",
            fontWeight: 600,
            fontFamily: "Inter",
          }}
        >
          Overall EEAT
        </Typography>
        <Box
          display="flex"
          flexDirection="row"
          gap="8px"
          mt="0.5rem"
          mb="0.5rem"
        >
          <Box sx={{ mb: 1 }}>
            <ScoreCircle score={parsedResult?.overall} size={24} />
          </Box>

          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: "600",
              fontFamily: "Inter",
              color: "#7B85AD",
              height: "32px",
            }}
          >
            {parsedResult?.overall ?? "N/A"}
          </Typography>
        </Box>
      </Box>

      {/* Learn more section */}
      <Typography
        sx={{
          fontWeight: 500,
          fontFamily: "Inter",
          fontSize: "18px",
          mb: "0.5rem",
          color: "#121828",
          textAlign: "left",
          mt: "1rem",
        }}
      >
        Ready to boost your score?
      </Typography>
      <Typography
        sx={{
          color: "#7B85AD",
          mb: "1.5rem",
          fontSize: "15px",
          fontFamily: "Inter",
          fontWeight: 400,
          textAlign: "left",
        }}
      >
        Access a full breakdown and AI-powered suggestions to raise your EEAT
        score and content quality.
      </Typography>
      <Button
        fullWidth
        variant="contained"
        onClick={handleSeeHowToImprove}
        sx={{
          backgroundImage:
            "linear-gradient(86.04deg,#FFD875 -18.56%, #DA00C8 35.8%, #6A00FF 106.2%)",
          color: "#fff !important",
          fontWeight: 600,
          fontSize: "1.1rem",
          borderRadius: "10px",
          py: 1.5,
          boxShadow: "0 8px 16px 0 rgba(218, 0, 200, 0.15)",
          mb: 2,
          textTransform: "none",
          fontFamily: "Inter",
          "&:hover": {
            backgroundImage:
              "linear-gradient(86.04deg,#FFD875 -18.56%, #DA00C8 35.8%, #6A00FF 106.2%)",
            boxShadow: "0 8px 16px 0 rgba(218, 0, 200, 0.25)",
          },
        }}
      >
        Show Me How to Improve My Score
      </Button>
      <Box
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <InfoOutlinedIcon sx={{ color: "#919EAB", fontSize: 18, mr: 1 }} />
        <Typography
          sx={{
            color: "#919EAB",
            fontSize: "14px",
            fontFamily: "Inter",
            fontWeight: 400,
          }}
        >
          Free. No credit card required.
        </Typography>
      </Box>
    </>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 500,
          width: "100%",
          borderRadius: 4,
          boxShadow: "none",
          border: "1px dashed rgba(201, 195, 235, 0.8)",
          p: { xs: 2.5, sm: 4 },
        }}
      >
        {parsedResult && !isLoading ? renderResultView() : renderInputView()}
      </Card>
    </Box>
  );
};

export default EEAT;
